import {FlexPage} from '@hconnect/common/components/FlexPage'
import {noSideCard, withSideCard} from '@hconnect/common/consts'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {CardBox, Column} from '@hconnect/uikit/src/lib2'
import {Button, clx, icon, spawnSuccessToast, useMediaQuery} from '@hm/fundamental'
import {TFunction} from 'i18next'
import {FC, useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {EventProcessStageTitle} from '../../components/common/EventProcessStageTitle'
import {DocumentCreate} from '../../components/documents/DocumentCreate'
import {DocumentDetails} from '../../components/documents/DocumentDetails'
import {DocumentEdit} from '../../components/documents/DocumentEdit'
import {DocumentLink} from '../../components/documents/DocumentLink'
import {DocumentStatus} from '../../components/documents/DocumentStatus'
import {FEATURE_NAMESPACE} from '../../consts'
import {useInstructionsPageLastVisited} from '../../hooks/useInstructionsPageLastVisited'
import {useTrackDocumentsAnalytics} from '../../hooks/useTrackDocumentsAnalytics'
import {ViewMode} from '../../types'
import {Instruction} from '../../types/documents.types'
import {ColumnsMap} from '../../types/table.types'

import {DocumentDelete} from './DocumentDelete'
import {DocumentsTable} from './DocumentsTable'

// Column spans mirror tableWithSideCard / sideCardSize from @hconnect/common/consts,
// using MUI's breakpoints (md 900, lg 1200, xl 1536) instead of Tailwind's defaults
const TABLE_WITH_SIDE_CARD_CLASSES =
  'col-span-12 min-[900px]:col-span-6 min-[1200px]:col-span-7 min-[1536px]:col-span-8'
const SIDE_CARD_CLASSES =
  'col-span-12 min-[900px]:col-span-6 min-[1200px]:col-span-5 min-[1536px]:col-span-4'

const generateTitleColumn = (t: TFunction): Column<Instruction> => ({
  key: 'name',
  label: t('documents.label.documentLink'),
  sortable: true,
  customTemplate: (rowData) => <DocumentLink document={rowData} />
})
const generateStatusColumn = (t: TFunction): Column<Instruction> => ({
  key: 'status',
  label: t('documents.label.status'),
  sortable: true,
  customTemplate: (rowData) => <DocumentStatus document={rowData} />
})
const generateCategoryColumn = (t: TFunction): Column<Instruction> => ({
  key: 'category',
  label: t('documents.label.category'),
  sortable: true,
  customTemplate: ({category}) => (category ? t(`shiftEvent.category.${category}`) : '-')
})
const generateProcessStageColumn = (t: TFunction, plantId: string): Column<Instruction> => ({
  key: 'processStage',
  label: t('documents.label.processStage'),
  sortable: true,
  customTemplate: ({processStage}) => (
    <EventProcessStageTitle plantId={plantId} stage={processStage} />
  )
})
const generateColumnsMap = (t: TFunction, plantId: string): ColumnsMap<Instruction> => {
  return {
    XS: [generateTitleColumn(t)],
    S: [generateTitleColumn(t), generateStatusColumn(t)],
    M: [generateTitleColumn(t), generateProcessStageColumn(t, plantId), generateStatusColumn(t)],
    L: [
      generateTitleColumn(t),
      generateProcessStageColumn(t, plantId),
      generateCategoryColumn(t),
      generateStatusColumn(t)
    ],
    XL: [
      generateTitleColumn(t),
      generateProcessStageColumn(t, plantId),
      generateCategoryColumn(t),
      generateStatusColumn(t)
    ]
  }
}

type DocumentsViewProps = {
  plantId: string
  country: string
  isLoading: boolean
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  documents?: Instruction[]
}

export const DocumentsView: FC<DocumentsViewProps> = ({
  plantId,
  country,
  isLoading,
  documents,
  viewMode,
  setViewMode
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const trackAnalyticsEvent = useTrackDocumentsAnalytics({plantId, country})
  const isMobile = useMediaQuery({breakpoint: 'md', upTo: true})

  const [showDeleteDialog, setDeleteDialog] = useState<boolean>(false)

  const {setLastVisited} = useInstructionsPageLastVisited()

  const breakPoint = useWidth()
  const isSideCardShown = viewMode.mode !== 'tableOnlyMode'
  const tableSize = (isSideCardShown ? withSideCard : noSideCard).get(breakPoint) ?? 'S'
  const columns = useMemo<ColumnsMap<Instruction>>(
    () => generateColumnsMap(t, plantId),
    [t, plantId]
  )

  const onRowClick = useCallback(
    ({id}: Instruction) => {
      setViewMode({mode: 'detailsMode', itemId: id})
      trackAnalyticsEvent('userOpensInstructionDetails', {instructionId: id})
    },
    [setViewMode, trackAnalyticsEvent]
  )

  const onDeleteSuccess = useCallback(() => {
    setViewMode({mode: 'tableOnlyMode'})
    setDeleteDialog(false)
    spawnSuccessToast(t('documents.action.deleteSuccess'))
  }, [setViewMode, t])

  const isTableShown = !isSideCardShown || !isMobile
  const selectedDocument =
    viewMode.mode === 'detailsMode' || viewMode.mode === 'editMode'
      ? (documents || []).find((document) => document.id === viewMode.itemId)
      : undefined

  const closeSideCard = () => {
    if (viewMode.mode === 'editMode') {
      setViewMode({mode: 'detailsMode', itemId: viewMode.itemId})
    } else {
      setViewMode({mode: 'tableOnlyMode'})
    }
  }

  const onSuccessSubmit = (itemId: string) => {
    setViewMode({mode: 'detailsMode', itemId})
    spawnSuccessToast(t('documents.label.createSuccess'))
    setLastVisited()
  }

  const onEdit = (id: string) => {
    setViewMode({mode: 'editMode', itemId: id})
    setLastVisited()
  }

  const onSuccessEdit = () => {
    closeSideCard()
    spawnSuccessToast(t('documents.label.editSuccess'))
    setLastVisited()
  }
  const getCardContent = () => {
    if (viewMode.mode === 'detailsMode' && selectedDocument) {
      return (
        <DocumentDetails
          plantId={plantId}
          country={country}
          document={selectedDocument}
          onClose={closeSideCard}
          onEdit={onEdit}
          onDelete={() => setDeleteDialog(true)}
        />
      )
    }

    if (viewMode.mode === 'createMode') {
      return (
        <DocumentCreate
          plantId={plantId}
          country={country}
          doClose={closeSideCard}
          onSuccessSubmit={onSuccessSubmit}
        />
      )
    }

    if (viewMode.mode === 'editMode' && selectedDocument) {
      return (
        <DocumentEdit
          plantId={plantId}
          country={country}
          document={selectedDocument}
          doClose={closeSideCard}
          onSuccessSubmit={onSuccessEdit}
        />
      )
    }
  }

  return (
    <FlexPage title={t('documents.pageName')} appName="Cockpit">
      <div className="grid grow grid-cols-12 gap-4 overflow-hidden">
        {isTableShown && (
          <div
            className={clx(
              'h-full',
              isSideCardShown ? TABLE_WITH_SIDE_CARD_CLASSES : 'col-span-12'
            )}
          >
            <CardBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <div className="mb-4 flex justify-end">
                <Button
                  variant="tertiary"
                  type="button"
                  data-cy="document-create"
                  onClick={() => {
                    setViewMode({mode: 'createMode'})
                  }}
                  icon={icon.mdiPlus}
                >
                  {t('documents.action.create')}
                </Button>
              </div>
              <DocumentsTable
                documents={documents}
                isLoading={isLoading}
                columns={columns[tableSize]}
                onRowClick={onRowClick}
                selectedDocumentId={selectedDocument?.id}
              />
            </CardBox>
          </div>
        )}

        {isSideCardShown && (viewMode.mode as string) !== 'tableOnlyMode' && (
          <div className={clx('h-full overflow-hidden', SIDE_CARD_CLASSES)}>{getCardContent()}</div>
        )}
      </div>

      {!!(showDeleteDialog && selectedDocument) && (
        <DocumentDelete
          plantId={plantId}
          country={country}
          onSuccess={onDeleteSuccess}
          setDialogOpen={setDeleteDialog}
          instruction={selectedDocument}
          showDialog={true}
        />
      )}

      <p data-test-id="page-document-mode-indicator" style={{display: 'none'}}>
        {/* shall be used in cypress tests to validate the mode this page is in */}
        {viewMode.mode}
      </p>
    </FlexPage>
  )
}
